import { Box, Flex, Heading, Link, useColorModeValue, HStack, Text, VStack, Spinner } from '@chakra-ui/react';
import { Link as RouterLink, useLocation, Outlet } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import PreferencesDrawer from './PreferencesDrawer';
import ColorModeToggle from './ColorModeToggle';
import { useVulnerabilityData, DataSource } from '../hooks/useVulnerabilityData';
import { useVulnerabilities } from '../context/VulnerabilityContext';

export interface LayoutOutletContext {
  source: DataSource;
}
export default function Layout() {
  const location = useLocation();
  const headerBg = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const pageBg = useColorModeValue('gray.50', 'black');
  const { setVulnerabilities } = useVulnerabilities();
  const { data, isLoading, error, source } = useVulnerabilityData('/ui_demo.json');
  const loadingHeadingColor = useColorModeValue('gray.700', 'gray.100');
  const loadingSubtextColor = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (data) {
      setVulnerabilities(data);
    }
  }, [data, setVulnerabilities]);

  const outletContext = useMemo<LayoutOutletContext>(() => ({ source }), [source]);

  return (
    <Box minH="100vh" bg={pageBg} transition="background-color 0.2s ease">
      <Box
        as="header"
        bg={headerBg}
        borderBottom="1px"
        borderColor={borderColor}
        px={{ base: 4, md: 6 }}
        py={{ base: 3, md: 4 }}
        position="sticky"
        top={0}
        zIndex={1000}
        boxShadow="sm"
      >
        <Flex
          justify="space-between"
          align="center"
          maxW="1400px"
          mx="auto"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 3, md: 0 }}
        >
          <VStack align={{ base: 'center', md: 'start' }} spacing={0}>
            <Heading size={{ base: 'md', md: 'lg' }} color="brand.600">
              Vulnerability Visualizer
            </Heading>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">
              Security posture at a glance
            </Text>
          </VStack>
          <HStack spacing={{ base: 2, md: 4 }} align="center" flexWrap="wrap" justify={{ base: 'center', md: 'flex-end' }}>
            <Link
              as={RouterLink}
              to="/"
              fontWeight={location.pathname === '/' ? 'bold' : 'normal'}
              color={location.pathname === '/' ? 'brand.600' : 'gray.600'}
              _hover={{ color: 'brand.600' }}
              fontSize={{ base: 'sm', md: 'md' }}
            >
              Dashboard
            </Link>
            <Link
              as={RouterLink}
              to="/compare"
              fontWeight={location.pathname === '/compare' ? 'bold' : 'normal'}
              color={location.pathname === '/compare' ? 'brand.600' : 'gray.600'}
              _hover={{ color: 'brand.600' }}
              fontSize={{ base: 'sm', md: 'md' }}
            >
              Compare
            </Link>
            <ColorModeToggle />
            <PreferencesDrawer />
          </HStack>
        </Flex>
      </Box>
      <Box as="main" maxW="1400px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 4, md: 8 }} transition="background-color 0.2s ease">
        {isLoading ? (
          <VStack spacing={3} py={20} align="center" color={loadingSubtextColor}>
            <Spinner size="xl" thickness="4px" speed="0.8s" color="brand.500" />
            <Heading size="md" color={loadingHeadingColor}>
              Loading vulnerability data…
            </Heading>
            <Text fontSize="sm">
              Fetching the latest dataset (falls back to the bundled copy if the upstream source is unreachable)
            </Text>
          </VStack>
        ) : error ? (
          <VStack spacing={4} py={20} align="center">
            <Heading size="md" color="red.500">
              Error loading data
            </Heading>
            <Text color="gray.500" fontSize="sm" textAlign="center">
              Note: If ui_demo.json is a Git LFS file, ensure you have pulled the actual file content.
            </Text>
          </VStack>
        ) : (
          <Outlet context={outletContext} />
        )}
      </Box>
    </Box>
  );
}

